import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Check } from "lucide-react";
import { BUSINESS, TRUST_BADGES, IMAGES } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

const LINES = ["Wholesale Supplier", "of Onion, Potato,", "Ginger, Garlic & Red Chilli"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: EASE } }),
};

const waLink = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(BUSINESS.whatsappMessage)}`;

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.8]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={IMAGES.heroBg} alt="M N Savanur wholesale vegetable shop, APMC Market Yard, Hubli" className="h-full w-full object-cover object-center" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0b0f0b] via-[#0b0f0b]/75 to-[#0b0f0b]/25" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0b0f0b] via-transparent to-[#0b0f0b]/40" />

      <div className="mx-auto w-full max-w-[1400px] px-6 pb-16 pt-32 md:px-12 md:pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7 }}>
          <span className="overline text-[var(--gold)]">M N Savanur · Hubli, Karnataka</span>
        </motion.div>

        <h1 className="mt-5 font-display text-[2.6rem] font-bold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-[5.2rem]">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineVariant} initial="hidden" animate="show" custom={i} className="block">
                {i === 2 ? (
                  <>
                    Ginger, Garlic & <span className="italic text-[var(--gold)]">Red Chilli</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="mt-7 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg"
        >
          <span className="mb-3 block text-sm font-semibold uppercase tracking-wide text-[var(--gold)]">
            Wholesale • Retail • Hotel Supply • Restaurant Supply • Catering Supply
          </span>
          We supply premium quality fresh vegetables directly from APMC to homes, restaurants,
          hotels, caterers and retailers across Hubballi and nearby areas.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <a
            href={`tel:${BUSINESS.phone}`}
            data-testid="hero-call-now"
            className="group flex items-center gap-2.5 rounded-full bg-[var(--gold)] px-7 py-4 text-base font-semibold text-[#1a1a1a] transition-all duration-300 hover:shadow-xl hover:shadow-yellow-900/30"
          >
            <Phone size={18} className="transition-transform group-hover:rotate-12" /> Call Now
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-order"
            className="group flex items-center gap-2.5 rounded-full border border-white/40 bg-white/5 px-7 py-4 text-base font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-[#25D366] hover:border-[#25D366]"
          >
            <MessageCircle size={18} />
            WhatsApp Order
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/15 pt-6"
        >
          {TRUST_BADGES.map((b) => (
            <div key={b} data-testid={`trust-badge-${b.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-2 text-sm font-medium text-white/90">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--green)]">
                <Check size={12} className="text-white" />
              </span>
              {b}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
